import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { InboxMessage, Customer, Notification, Profile } from '@/lib/integrations/supabase/types';

/**
 * Get inbox messages for a business.
 */
export async function getInboxMessages(
  businessId: string,
  limit: number = 50
): Promise<(InboxMessage & { customer?: Customer | null })[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('inbox_messages')
    .select(`
      *,
      customer:customers(*)
    `)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Get unread message count.
 */
export async function getUnreadMessageCount(businessId: string): Promise<number> {
  const supabase = getBrowserClient();

  const { count, error } = await supabase
    .from('inbox_messages')
    .select('*', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .is('read_at', null);

  if (error) {
    throw error;
  }

  return count || 0;
}

/**
 * Mark a message as read.
 */
export async function markMessageAsRead(messageId: string): Promise<void> {
  const supabase = getBrowserClient();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase
    .from('inbox_messages') as any)
    .update({ read_at: new Date().toISOString() })
    .eq('id', messageId);

  if (error) {
    throw error;
  }
}

/**
 * Mark all messages as read.
 */
export async function markAllMessagesAsRead(businessId: string): Promise<void> {
  const supabase = getBrowserClient();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase
    .from('inbox_messages') as any)
    .update({ read_at: new Date().toISOString() })
    .eq('business_id', businessId)
    .is('read_at', null);

  if (error) {
    throw error;
  }
}

/**
 * Get notifications for a user in their current business.
 * Note: RLS policies ensure business isolation, but we add business_id filter for extra safety.
 */
export async function getNotifications(
  userId: string,
  limit: number = 50
): Promise<Notification[]> {
  const supabase = getBrowserClient();

  const { data: profile } = await supabase
    .from('profiles')
    .select('business_id')
    .eq('id', userId)
    .single<Pick<Profile, 'business_id'>>();

  if (!profile?.business_id) {
    return [];
  }

  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('recipient_user_id', userId)
    .eq('business_id', profile.business_id)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Get unread notification count for a user in their current business.
 * Note: RLS policies ensure business isolation, but we add business_id filter for extra safety.
 */
export async function getUnreadNotificationCount(userId: string): Promise<number> {
  const supabase = getBrowserClient();

  const { data: profile } = await supabase
    .from('profiles')
    .select('business_id')
    .eq('id', userId)
    .single<Pick<Profile, 'business_id'>>();

  if (!profile?.business_id) {
    return 0;
  }

  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('recipient_user_id', userId)
    .eq('business_id', profile.business_id)
    .eq('status', 'unread');

  if (error) {
    throw error;
  }

  return count || 0;
}

/**
 * Mark a notification as read.
 */
export async function markNotificationRead(notificationId: string): Promise<void> {
  const supabase = getBrowserClient();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase
    .from('notifications') as any)
    .update({ status: 'read' })
    .eq('id', notificationId);

  if (error) {
    throw error;
  }
}

/**
 * Mark all notifications as read for a user.
 */
export async function markAllNotificationsRead(userId: string): Promise<void> {
  const supabase = getBrowserClient();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase
    .from('notifications') as any)
    .update({ status: 'read' })
    .eq('recipient_user_id', userId)
    .eq('status', 'unread');

  if (error) {
    throw error;
  }
}

/**
 * Delete a single notification by ID.
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  const supabase = getBrowserClient();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase
    .from('notifications') as any)
    .delete()
    .eq('id', notificationId);

  if (error) {
    throw error;
  }
}

/**
 * Delete all notifications for a business via Route Handler.
 */
export async function deleteAllNotifications(businessId: string): Promise<void> {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`/api/business/${businessId}/notifications`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete all notifications');
  }
}
